/**
 * Der Weg zurück aus dem geschlossenen Modus (v1.3-N-11; ADR-0013).
 *
 * Der Knopf „blind" wird ohne Rückfrage wirksam — ein Fehlgriff ließe
 * die ganze Revision ohne fremde Befunde und ohne Wärmebild durchzuarbeiten oder
 * unbearbeitet liegen. Die **Rücknahme** ist dieser Weg zurück, und sie gilt
 * genau so lange, wie nicht abgegeben ist.
 *
 * Er steht hier und nicht zweimal, weil er an **zwei** Flächen gehört: in der
 * Zeile des Revisionsstroms neben dem Abzeichen „blind" und im Kopf des
 * Befundprotokolls, wo das Schloss steht und der Satz, der den geschlossenen
 * Modus benennt. Das ist die Stelle, an der man merkt, dass nichts zu sehen ist.
 * Zwei Kopien wären zwei Gelegenheiten, denselben Satz verschieden zu schreiben.
 *
 * **Nur, wenn die Blindheit aus der Absicht kommt.** Wer die Eigenschaft
 * `blind_review` trägt, arbeitet in jeder Revision blind und hat nichts gedrückt,
 * was zurückzunehmen wäre; für ihn steht der Knopf gar nicht erst da. Der Server
 * sagt das in `blind_requested`, und die Oberfläche rechnet es nicht selbst aus.
 *
 * **Ohne Rückfrage**, wie das Gegenstück: Sie ist folgenarm und wiederholbar —
 * die Rückfrage steht dort, wo etwas verbraucht wird.
 */

import type { ReactElement } from "react";

import { Button } from "../design-system";

/** Die Handlung, wie sie auf dem Knopf steht — das Gegenstück zum Knopf „blind". */
export const WITHDRAW_BLIND = "blind zurücknehmen";

/**
 * Die Folge, ausgeschrieben — im Titel, nicht auf dem Knopf.
 *
 * Auf dem Knopf steht die Handlung, im Titel ihre Folge, wie beim Setzen. Die
 * Folge ist hier eine doppelte: Was zurückgehalten wurde, erscheint sofort, und
 * der Weg hinein bleibt offen — die Rücknahme stellt den Normalzustand her, sie
 * erfindet keinen zweiten.
 */
export const WITHDRAW_BLIND_TITLE =
  "Diese Revision doch nicht blind durchgehen: Fremde Befunde und das Wärmebild erscheinen " +
  "sofort. Blind bleibt danach möglich, solange Ihnen keiner ausgeliefert wurde.";

/**
 * `onWithdraw` nimmt die Absicht zurück. `busy` graut den Knopf aus, solange ein
 * Aufruf läuft — die Zeile im Strom kennt keinen und lässt es weg.
 *
 * Das Klickereignis wird angehalten: Im Strom sitzt der Knopf in einer Zeile,
 * die sonst die Revision öffnet, und hier soll er nur das eine tun, was auf ihm
 * steht. Im Befundprotokoll ist das folgenlos — eine Fläche, die darauf hörte,
 * gibt es dort nicht.
 */
export function WithdrawBlindButton({
  onWithdraw,
  busy = false,
}: {
  onWithdraw: () => void;
  busy?: boolean;
}): ReactElement {
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={busy}
      title={WITHDRAW_BLIND_TITLE}
      icon="undo-2"
      onClick={(event) => {
        event.stopPropagation();
        onWithdraw();
      }}
    >
      {WITHDRAW_BLIND}
    </Button>
  );
}
